import { ReactNode, useCallback, useMemo } from 'react';
import { DetailsProps } from 'types/requestDetails';
import { WorkOrderLine } from 'types/serviceRequest';

import { Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { WithLoadingPropTypes } from 'app/hocs/withLoading';
import Loader from 'app/shared/components/Loader';

import { useSelector } from 'hooks/global';
import { useAppTranslation } from 'hooks/useAppTranslation';

import { selectRequestDetails } from 'store/selectors/serviceRequests.selector';

import { WorkOrderType } from 'constants/requestDetails';

import formatValueToCurrency from 'utilities/formatValueToCurrency';

const Details = ({
  isLoading,
  t,
  totalValues,
  isTotalDetails,
  tableConfig,
  getCellContentValue,
}: DetailsProps & Partial<WithLoadingPropTypes>) => {
  const { amount } = useSelector(selectRequestDetails);
  const { t: translateStatus } = useAppTranslation('dashboard');

  const tableKeys = useMemo(() => Object.keys(tableConfig), [tableConfig]);

  const renderTotalsRow = useCallback(
    () =>
      isTotalDetails && amount ? (
        <TableRow className='footer-row'>
          <TableCell colSpan={7} align='right'>
            {t('tableFooter.total')}
          </TableCell>
          <TableCell colSpan={2} align='center'>
            {formatValueToCurrency(amount)}
          </TableCell>
        </TableRow>
      ) : null,
    [amount, isTotalDetails, t]
  );

  const renderTableCells = useCallback(
    (getValue: (key: string) => ReactNode) =>
      tableKeys.map((key, idx: number) => {
        const celProps = tableConfig[
          key as keyof typeof tableConfig
        ] as TableCellProps;

        return (
          <TableCell key={idx} {...celProps}>
            {getValue(key)}
          </TableCell>
        );
      }),
    [tableConfig, tableKeys]
  );

  const renderWorkOrderLineRows = useCallback(
    (lines: Array<Partial<WorkOrderLine>>): Array<ReactNode> | null => {
      return lines?.map((line: Partial<WorkOrderLine>, index: number) => {
        const { childs, costs } = line;

        const isEmptyRow = line.type === WorkOrderType.Empty;
        const shouldShowRow =
          (line.isPlaceholder && !line.childs?.length) || !line.isPlaceholder;

        const extendedLine = {
          ...line,
          ...costs,
          childs,
        };

        return (
          <>
            {shouldShowRow && (
              <TableRow
                className={isEmptyRow ? 'is-empty-row' : ''}
                key={index}
              >
                {renderTableCells(
                  getCellContentValue(extendedLine, translateStatus)
                )}
              </TableRow>
            )}

            {childs?.length ? renderWorkOrderLineRows(childs) : null}
          </>
        );
      });
    },
    [getCellContentValue, renderTableCells, translateStatus]
  );

  return (
    <TableContainer component={Paper}>
      <Table className='rob-totalen-table' aria-label='rob-totalen-table'>
        <TableHead>
          <TableRow>
            {renderTableCells(key =>
              t(`tableHeader.${key as keyof typeof tableConfig}`)
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={tableKeys.length} align='center'>
                <Loader width='50px' height='50px' />
              </TableCell>
            </TableRow>
          ) : totalValues.length ? (
            totalValues.map((row, index) => {
              const { workOrderLines } = row;

              const isWorkorderLineVisible = !isTotalDetails && workOrderLines;

              const rowClassName = !isTotalDetails ? 'rob-row' : '';

              return (
                <>
                  <TableRow className={rowClassName} key={index}>
                    {!!row &&
                      renderTableCells(
                        getCellContentValue(row, translateStatus)
                      )}
                  </TableRow>

                  {isWorkorderLineVisible &&
                    renderWorkOrderLineRows(workOrderLines)}
                </>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={tableKeys.length} align='center'>
                <Typography align='center'>{t('emptyData')}</Typography>
              </TableCell>
            </TableRow>
          )}
          {renderTotalsRow()}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Details;
